import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { DownloadCloud, LogOut, Settings } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { useIsMobile } from '@/hooks/use-mobile';

const Header: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  
  return (
    <header className="sticky top-0 z-50 w-full border-b backdrop-blur-lg bg-white/30 border-white/40 dark:bg-black/20 dark:border-white/10">
      <div className="container-pad flex items-center justify-between h-16 md:h-20">
        <div className="flex-1 flex justify-start">
          <ThemeToggle />
        </div>
        
        <div className="flex-1 flex justify-center">
          <Link to={isAuthenticated ? "/dashboard" : "/"} className="flex items-center gap-2 group">
            <div className="flex items-center justify-center rounded-xl shadow-md group-hover:shadow-lg transition-all overflow-hidden h-10 w-10">
              <img src="/images/panda-logo.svg" alt="Panda Logo" className="h-full w-full object-contain" />
            </div>
            <div className={`relative ${isMobile ? 'hidden' : 'block'}`}>
              <span className="text-lg font-bold tracking-tight bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                Attachment Panda
              </span>
              <div className="absolute -bottom-1 left-0 w-0 group-hover:w-full h-0.5 bg-primary/30 transition-all duration-300"></div>
            </div>
          </Link>
        </div>
        
        <div className="flex-1 flex justify-end items-center">
          {isAuthenticated && user ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" className="rounded-full w-10 h-10 p-0">
                  <Avatar className="h-9 w-9 transition-all hover:ring-2 hover:ring-primary/20">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback>{user.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2" align="end">
                <div className="flex flex-col space-y-1">
                  <div className="px-2 py-1.5 text-sm font-medium">{user.name}</div>
                  <div className="px-2 pb-1.5 text-xs text-muted-foreground">{user.email}</div>
                  <div className="h-px bg-border my-1"></div>
                  <Link 
                    to="/settings"
                    className="flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-secondary transition-colors"
                  >
                    <Settings size={16} />
                    <span>Settings</span>
                  </Link>
                  <button 
                    className="flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-secondary transition-colors"
                    onClick={logout}
                  >
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <div className="flex items-center gap-2">
              {location.pathname !== '/' && (
                <Button asChild variant="secondary">
                  <Link to="/">Sign In</Link>
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
